// ABOUTME: Detail pane of one onboarding chapter: header with icon and step counter, the step
// ABOUTME: list with status icons, the selected step's content box, and the finish-later / continue actions.

import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { Box, ButtonBase, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import MAddPrinter from "../../../../../components/global/Modals/MAddPrinter";
import MAddWarehouse from "../../../../../components/global/Modals/MAddWarehouse";
import MAddWarehouseLocationType from "../../../../../components/global/Modals/MAddWarehouseLocationType";
import MAddWarehouseZone from "../../../../../components/global/Modals/MAddWarehouseZone";
import MAddWorkspace from "../../../../../components/global/Modals/MAddWorkspace";
import MAddCarrier from "../../../../../components/global/Modals/carriers/AddCarrier";
import MAddCost from "../../../../../components/global/Modals/MAddCost";
import MAddInvoiceCategory from "../../../../../components/global/Modals/MAddInvoiceCategory";
import MAddInvoiceProduct from "../../../../../components/global/Modals/MAddInvoiceProduct";
import MpAddShipper from "../../../../../components/global/ModalsProgression/MpAddShipper/MpAddShipper";
import MpAddShipperProfile from "../../../../../components/global/ModalsProgression/MpAddShipperProfile/MpAddShipperProfile";
import MpAddStream from "../../../../../components/global/ModalsProgression/MpAddStream/MpAddStream";
import SidepanelLoader from "../../../../../components/theme/sidepanel2/SidepanelLoader";
import useManualSettingsSave from "../../../../../components/global/Settings/useManualSettingsSave";
import Shbutton from "../../../../../components/theme/buttons/Shbutton";
import ShLabel from "../../../../../components/theme/label/ShLabel";
import Subheading from "../../../../../components/theme/text/Subheading";
import Text from "../../../../../components/theme/text/Text";
import { useUpdateOnboardingStepMutation } from "../../../../../newapi/warehouse/onboardingSlice";
import { tokens } from "../../../../../theme";
import { showToastMessageRequestError } from "../../../../../utils/toasts";
import { ChapterIcon, currentStepNumber, panelSx } from "./ChapterCard";
import StepFields from "./StepFields";

const SpStreamDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpStreamDetails/SpStreamDetails"));
const SpShipperDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpShipperDetails/SpShipperDetails"));

const STEP_STATUS_LABEL = {
    done: { key: "onboarding_step_done", variant: "green" },
    skipped: { key: "onboarding_step_skipped", variant: "orange" },
    open: { key: "onboarding_step_open", variant: "grey" },
};

// The existing add modals a step can open in place of its target screen, keyed by the
// `modal` name the backend registry gives the step.
const STEP_MODALS = {
    warehouse: [{ label: "new_warehouse", Component: MAddWarehouse }],
    warehouselocationtype: [{ label: "new_warehouse_location_type", Component: MAddWarehouseLocationType }],
    warehousezone: [{ label: "new_zone", Component: MAddWarehouseZone }],
    printer: [{ label: "new_printer", Component: MAddPrinter }],
    workspace: [{ label: "new_workspace", Component: MAddWorkspace }],
    shipper: [{ label: "new_shipper", Component: MpAddShipper }],
    shipperprofile: [{ label: "new_shipper_profile", Component: MpAddShipperProfile }],
    carrier: [{ label: "new_carrier", Component: MAddCarrier }],
    // A stream is finished in its side panel (rules, shippers), so it opens right after creation.
    stream: [{ label: "add_stream", Component: MpAddStream, opensStream: true }],
    invoiceproduct: [{ label: "new_invoice_product", Component: MAddInvoiceProduct }],
    invoicecategory: [{ label: "new_category", Component: MAddInvoiceCategory }],
    // Financial steps open the invoice product and cost modals with the type they are about.
    invoiceproduct_return_credit: [
        { label: "new_invoice_product", Component: MAddInvoiceProduct, props: { preset: { type: "return_payment", subType: "return_payment" } } },
    ],
    invoiceproduct_return_transaction: [
        { label: "new_invoice_product", Component: MAddInvoiceProduct, props: { preset: { type: "return_transaction_cost", subType: "return_transaction_cost" } } },
    ],
    return_processing_cost: [
        { label: "new_invoice_product", Component: MAddInvoiceProduct, props: { preset: { type: "default", subType: "return_processing" } } },
        { label: "new_cost", Component: MAddCost, props: { preset: { type: "return" } } },
    ],
    inbound_manual_cost: [
        { label: "new_invoice_product", Component: MAddInvoiceProduct, props: { preset: { type: "default", subType: "inbound" } } },
        { label: "new_cost", Component: MAddCost, props: { preset: { type: "inbound_manual" } } },
    ],
    inbound_processing_cost: [
        { label: "new_invoice_product", Component: MAddInvoiceProduct, props: { preset: { type: "default", subType: "inbound_processing" } } },
        { label: "new_cost", Component: MAddCost, props: { preset: { type: "inbound_processing" } } },
    ],
    barcode_print_cost: [
        { label: "new_invoice_product", Component: MAddInvoiceProduct, props: { preset: { type: "default", subType: "general" } } },
        { label: "new_cost", Component: MAddCost, props: { preset: { type: "barcode" } } },
    ],
    adjustment_charge_cost: [
        { label: "new_invoice_product", Component: MAddInvoiceProduct, props: { preset: { type: "default", subType: "adjustment_charges" } } },
        { label: "new_cost", Component: MAddCost, props: { preset: { type: "adjustment_charges" } } },
    ],
};

const formatDetailValue = (t, value) => {
    if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : t("none");
    if (value === true) return t("yes");
    if (value === false) return t("no");
    if (value === null || typeof value === "undefined") return "-";
    return `${value}`;
};

const StepStatusIcon = ({ status, current }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const sx = { fontSize: 18, flexShrink: 0 };

    if (status === "done") return <CheckCircleOutlineIcon sx={{ ...sx, color: colors.green[400] }} />;
    if (status === "skipped") return <RemoveCircleOutlineIcon sx={{ ...sx, color: colors.orange[400] }} />;
    if (current) return <RadioButtonCheckedIcon sx={{ ...sx, color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400] }} />;
    return <RadioButtonUncheckedIcon sx={{ ...sx, color: colors.txt["secondary"] }} />;
};

const StepDetail = ({ chapter, onRefetch }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const warehouseId = useSelector((state) => state.warehouse.activeId);

    const [updateStep, { isLoading: isUpdating }] = useUpdateOnboardingStepMutation();
    const { handleChange, hasChanges, isSaving, save } = useManualSettingsSave();
    const [selectedStepKey, setSelectedStepKey] = useState(null);
    const [openModal, setOpenModal] = useState(null);
    // The stream side panel, opened for a stream created from this pane.
    const [streamId, setStreamId] = useState(null);
    const [streamPanelExtended, setStreamPanelExtended] = useState(false);
    // The shipper side panel, opened for a shipper that still misses a cost rule.
    const [shipperId, setShipperId] = useState(null);
    const [shipperPanelExtended, setShipperPanelExtended] = useState(false);

    // Land on the first open step of a chapter; keep the selection while the chapter refreshes.
    useEffect(() => {
        const stillThere = chapter.steps.some((step) => step.key === selectedStepKey);
        if (!stillThere) {
            const first = chapter.steps.find((step) => step.status === "open") ?? chapter.steps[0];
            setSelectedStepKey(first?.key ?? null);
        }
    }, [chapter, selectedStepKey]);

    const step = chapter.steps.find((item) => item.key === selectedStepKey);
    if (!step) {
        return null;
    }

    const stepLabel = STEP_STATUS_LABEL[step.status] ?? STEP_STATUS_LABEL.open;
    const hasFields = step.fields && step.fields.length > 0;
    const hasDetail = step.detail && Object.keys(step.detail).length > 0;
    const modals = STEP_MODALS[step.modal] ?? [];
    const related = step.related ?? [];

    const selectNextOpenStep = () => {
        const index = chapter.steps.findIndex((item) => item.key === step.key);
        const after = chapter.steps.slice(index + 1).find((item) => item.status === "open");
        const before = chapter.steps.slice(0, index).find((item) => item.status === "open");
        const next = after ?? before;
        if (next) {
            setSelectedStepKey(next.key);
        }
    };

    const setStatus = async (status) => {
        try {
            if (status === "done" && hasChanges) {
                await save();
            }
            await updateStep({ chapter: chapter.key, step: step.key, status }).unwrap();
            if (status !== "open") {
                selectNextOpenStep();
            }
        } catch (error) {
            showToastMessageRequestError(t("update_failed"), t, error);
        }
    };

    const closeModal = () => {
        setOpenModal(null);
        onRefetch();
    };

    const isBusy = isUpdating || isSaving;
    const isDark = theme.palette.mode === "dark";
    const divider = `1px solid ${isDark ? colors.grey[400] : colors.grey[100]}`;

    return (
        <Box
            sx={{
                ...panelSx(theme, colors),
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, padding: 2.5, paddingBottom: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <ChapterIcon chapterKey={chapter.key} status={chapter.status} />
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Subheading>{t(`onboarding_chapter_${chapter.key}`)}</Subheading>
                        <Text light>
                            {chapter.status === "finished"
                                ? t("onboarding_steps_completed", { done: chapter.doneCount, total: chapter.total })
                                : t("onboarding_step_of", { current: currentStepNumber(chapter), total: chapter.total })}
                        </Text>
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    "@media screen and (max-width: 63.9375em)": {
                        flexDirection: "column",
                    },
                }}
            >
                <Box
                    sx={{
                        width: 240,
                        flexShrink: 0,
                        padding: 2,
                        borderRight: divider,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        "@media screen and (max-width: 63.9375em)": {
                            width: 1,
                            borderRight: "none",
                            borderBottom: divider,
                        },
                    }}
                >
                    {chapter.steps.map((item) => {
                        const current = item.key === step.key;
                        return (
                            <ButtonBase
                                key={item.key}
                                onClick={() => setSelectedStepKey(item.key)}
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    gap: 1.5,
                                    width: 1,
                                    textAlign: "left",
                                    paddingX: 1.5,
                                    paddingY: 1,
                                    borderRadius: 3,
                                    background: current ? (isDark ? colors.grey[400] : colors.primary[80]) : "transparent",
                                }}
                            >
                                <StepStatusIcon status={item.status} current={current} />
                                <Text bold={current} light={!current && item.status !== "open"}>
                                    {t(`onboarding_step_${chapter.key}_${item.key}`)}
                                </Text>
                            </ButtonBase>
                        );
                    })}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0, padding: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Text light>{step.status === "open" ? t("onboarding_next_step") : t(stepLabel.key)}</Text>
                            <Subheading>{t(`onboarding_step_${chapter.key}_${step.key}`)}</Subheading>
                        </Box>
                        <ShLabel size={32} fitted variant={stepLabel.variant}>{t(stepLabel.key)}</ShLabel>
                    </Box>

                    {step.detected === true && <Text light>{t("onboarding_detected")}</Text>}
                    {step.override && step.detected !== true && (
                        <Text light>{`${t(STEP_STATUS_LABEL[step.override.status]?.key ?? "onboarding_step_open")} ${t("by")} ${step.override.by ?? "-"}`}</Text>
                    )}

                    <Box
                        sx={{
                            border: hasFields ? divider : `1px dashed ${isDark ? colors.grey[400] : colors.grey[200]}`,
                            borderRadius: 3,
                            padding: hasFields ? 2 : 4,
                            minHeight: hasFields ? 0 : 160,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: hasFields ? "flex-start" : "center",
                            alignItems: hasFields ? "stretch" : "center",
                            gap: 2,
                        }}
                    >
                        {hasFields ? (
                            <StepFields step={step} onChange={handleChange} />
                        ) : (
                            <Text light>{t("onboarding_no_fields")}</Text>
                        )}
                    </Box>

                    {hasDetail && (
                        <Box
                            sx={{
                                alignSelf: "flex-end",
                                minWidth: 280,
                                maxWidth: 1,
                                background: isDark ? colors.grey[400] : colors.grey[50],
                                borderRadius: 3,
                                paddingX: 2.5,
                                paddingY: 2,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                            }}
                        >
                            {Object.entries(step.detail).map(([key, value]) => (
                                <Box key={key} sx={{ display: "flex", justifyContent: "space-between", gap: 4 }}>
                                    <Text bold>{t(`onboarding_detail_${key}`)}</Text>
                                    <Text>{formatDetailValue(t, value)}</Text>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {(step.screen || modals.length > 0 || related.length > 0) && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {step.screen && (
                                <Shbutton
                                    variant="contained"
                                    color={isDark ? "blue" : "primary"}
                                    endIcon={<OpenInNewOutlinedIcon />}
                                    onClick={() => window.open(step.screen, "_blank", "noopener")}
                                >
                                    {step.screenKey ? t(`onboarding_open_${step.screenKey}`) : t("onboarding_open_screen")}
                                </Shbutton>
                            )}
                            {modals.map((modal) => (
                                <Shbutton
                                    key={modal.label}
                                    variant="contained"
                                    color="green"
                                    endIcon={<AddIcon />}
                                    onClick={() => setOpenModal(modal.label)}
                                >
                                    {t(modal.label)}
                                </Shbutton>
                            ))}
                            {related.map((item) => (
                                <Shbutton
                                    key={item.id}
                                    variant="outlined"
                                    color={isDark ? "blue" : "primary"}
                                    endIcon={<OpenInNewOutlinedIcon />}
                                    onClick={() => { setShipperId(item.id); setShipperPanelExtended(true); }}
                                >
                                    {t("onboarding_open_shipper_name", { name: item.name })}
                                </Shbutton>
                            ))}
                        </Box>
                    )}

                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap", marginTop: "auto", paddingTop: 1 }}>
                        {step.status === "open" ? (
                            <>
                                <Shbutton
                                    variant="contained"
                                    color="grey"
                                    disabled={isBusy}
                                    onClick={() => setStatus("skipped")}
                                >
                                    {t("onboarding_finish_later")}
                                </Shbutton>
                                <Shbutton
                                    variant="contained"
                                    color={isDark ? "blue" : "primary"}
                                    disabled={isBusy}
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={() => setStatus("done")}
                                >
                                    {t("onboarding_continue")}
                                </Shbutton>
                            </>
                        ) : (
                            <>
                                {hasChanges && (
                                    <Shbutton
                                        variant="contained"
                                        color={isDark ? "blue" : "primary"}
                                        disabled={isBusy}
                                        onClick={save}
                                    >
                                        {t("save")}
                                    </Shbutton>
                                )}
                                {step.detected !== true && (
                                    <Shbutton
                                        variant="contained"
                                        color="grey"
                                        disabled={isBusy}
                                        onClick={() => setStatus("open")}
                                    >
                                        {t("onboarding_reopen")}
                                    </Shbutton>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            {modals.map((modal) => (
                <modal.Component
                    key={modal.label}
                    {...(modal.props ?? {})}
                    warehouseId={warehouseId}
                    open={openModal === modal.label}
                    onOpen={() => setOpenModal(modal.label)}
                    onClose={closeModal}
                    handleClose={closeModal}
                    onCreated={modal.opensStream ? (id) => { setStreamId(id); setStreamPanelExtended(true); } : undefined}
                />
            ))}

            {streamId && (
                <SpStreamDetails
                    isExtended={streamPanelExtended}
                    setIsExtended={setStreamPanelExtended}
                    streamId={streamId}
                />
            )}

            {shipperId && (
                <SpShipperDetails
                    isExtended={shipperPanelExtended}
                    setIsExtended={setShipperPanelExtended}
                    shipperId={shipperId}
                />
            )}
        </Box>
    );
};

export default StepDetail;
